import { assertNotEmpty, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { Td, Tr } from '@festina-life/ui/listsAndTables'

import { useTemplateContext } from '../../context/templateContext'
import type { TemplateReference } from '../../types/templateModel'
import { isNotInherited } from '../../uiModels/templateOperations/types'
import { InheritableSelectEditor } from '../inheritableSelectEditor/InheritableSelectEditor'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateParentLink } from '../templateParentLink/TemplateParentLink'
import type { TemplateReferenceTableRowProps } from './types'

type MappingProps = {
  unusedKeyOptions: string[]
  unusedSourceOptions: string[]
}

export const TemplateReferenceMappingsTableRow = ({
  data,
  fieldPath,
  onChange,
  // Currently errors aren't being mapped by the backend on mappings. These will to be shown once the backend maps them correctly
  errors: _errors,
  unusedSourceOptions,
  unusedKeyOptions,
}: TemplateReferenceTableRowProps & MappingProps) => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  assertNotEmpty(templateVersion)

  const variantPath = `${fieldPath}[${data.source}]`

  const handleRemoveVariant = async () => {
    if (onChange) {
      await onChange(null)
    }
  }

  const handleKeyChanged = async (nextKeyValue: Optional<string>) => {
    const nextReferenceValue = {
      ...data,
      key: nextKeyValue,
    } satisfies TemplateReference

    await onChange?.(nextReferenceValue)
  }

  const handleSourceChanged = async (nextSourceValue: Optional<string>) => {
    const nextReferenceValue = {
      ...data,
      source: nextSourceValue,
    } satisfies TemplateReference

    await onChange?.(nextReferenceValue)
  }

  return (
    <Tr
      key={variantPath}
      // @beta hack to make the TD contents fill the full row height
      // https://stackoverflow.com/questions/3215553/make-a-div-fill-an-entire-table-cell
      className="relative size-full h-px align-top"
    >
      <Td className="relative flex h-full items-center gap-x-2">
        <InheritableSelectEditor
          options={unusedKeyOptions}
          value={data.key}
          fromOrigin={data.origin}
          isEditable={isDraftTemplate}
          onChange={handleKeyChanged}
          path={data.path}
          className="grow"
        />
        <InheritanceIndicator value={data} />
      </Td>
      <Td>
        <InheritableSelectEditor
          options={unusedSourceOptions}
          value={data.source}
          fromOrigin={data.origin}
          isEditable={isDraftTemplate}
          onChange={handleSourceChanged}
          path={data.path}
          className="grow"
          renderNonEditable={() =>
            data.source ? <TemplateParentLink templateId={data.source} /> : '-'
          }
        />
      </Td>
      {isDraftTemplate && !!data.origin && isNotInherited(data) && onChange && (
        <Td className="h-[inherit]">
          <IconButton
            onClick={handleRemoveVariant}
            iconSize="md"
            iconName="MinusCircle"
          />
        </Td>
      )}
    </Tr>
  )
}
